import React from 'react';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import useEmbedPdf from 'hooks/useEmbedPdf';
import useScrolledToBottomOfPdf from 'hooks/useScrolledToBottomOfPdf';
import { FIELDS_ROOT } from 'config/api';
import dialogActions from 'core/dialogs/actions';
import dataActions from 'core/data/actions';
import fieldActions from 'core/fields/actions';
import Button from 'common/Button';
import Spinner from 'common/Spinner';
import ErrorMsg from 'common/ErrorMsg';
import dialogStyles from './styles/dialog';
import contentStyles from './styles/content';
import bodyStyles from './styles/body';
import footerStyles from './styles/footer';
import noticeStyles from './styles/notice';
import embedPdf from './styles/embedPdf';

const styles = (theme) => {
  const common = {
    body: bodyStyles(theme),
    content: contentStyles(theme),
    dialog: dialogStyles(theme),
    embedPdf: embedPdf(theme),
    footer: footerStyles(theme),
    notice: noticeStyles(theme),
  };
  return {
    ...common,
    content: {
      ...common.content,
      borderLeft: 'none',
      display: 'flex',
    },
    dialog: {
      ...common.dialog,
      height: '90%',
      maxWidth: '100%',
      width: '90%',

      [`@media(max-width: ${theme.screenSizes.mobileMax}px)`]: {
        height: '100%',
        width: '100%',
      },
    },
  };
};

export const DefaultBare = ({
  changeFieldValue,
  classes,
  clientId,
  close,
  commonClasses,
  differentiator,
  disclosureAcceptLabel,
  disclosureCancelLabel,
  disclosureCloseLabel,
  fieldIsRequired,
  id,
  isReadOnly,
  title,
  validate,
  viewTheEntireDocumentLabel,
}) => {
  const addParam = differentiator ? `/${differentiator}` : '';
  useEmbedPdf(`${FIELDS_ROOT}/${id}/disclosurePdf${addParam}`, '#embed-pdf', {
    pdfOpenParams: {
      view: 'FitH',
    },
    title,
  });
  const isScrolledBottom = useScrolledToBottomOfPdf('#embed-pdf');

  const isViewOnly = differentiator || isReadOnly || !fieldIsRequired;

  const buttons = isViewOnly
    ? [
        {
          className: 'btn btn-mobile btn-i-accept',
          onClick: close,
          text: disclosureCloseLabel,
          variant: 'primary',
        },
      ]
    : [
        {
          className: 'btn btn-mobile btn-i-accept',
          disabled: !isScrolledBottom,
          onClick: () => {
            changeFieldValue(clientId, true);
            validate(clientId);
            close();
          },
          text: disclosureAcceptLabel,
          variant: 'primary',
        },
        {
          className: 'btn btn-mobile btn-mobile--secondary btn-i-decline',
          onClick: () => {
            changeFieldValue(clientId, fieldIsRequired ? '' : false);
            validate(clientId);
            close();
          },
          text: disclosureCancelLabel,
          variant: 'secondary',
        },
      ];

  return (
    <div className={classnames('modal-pdf', commonClasses, classes.dialog)}>
      <div className={classnames('modal-content', classes.content)}>
        <div className={classnames('modal-body', classes.body)} id="modal-body">
          <div>
            <div id="embed-pdf" className={classnames('embed-pdf', classes.embedPdf)} />
            <ErrorMsg type="page" />
          </div>
        </div>
        <div className={classnames('modal-footer', classes.footer)}>
          {!isViewOnly && (
            <p className={classnames('modal-notice', classes.notice)}>
              {viewTheEntireDocumentLabel}
            </p>
          )}
          {buttons.map((b, i) => (
            <Button
              /* eslint-disable-next-line react/no-array-index-key */
              key={`dialog-button-${i}`}
              className={`btn ${b.className} popbox-btn${i}`}
              data-key={i}
              onClick={b.onClick}
              disabled={!!b.disabled}
              variant={b.variant}
            >
              {b.text}
              {b.className === 'btn btn-mobile btn-i-accept' && (
                <>
                  <i className="icon-arrow-thin-right" aria-hidden="true" />
                  <Spinner loading={false} />
                </>
              )}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

DefaultBare.propTypes = {
  changeFieldValue: PropTypes.func.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  clientId: PropTypes.string,
  close: PropTypes.func.isRequired,
  commonClasses: PropTypes.string.isRequired,
  differentiator: PropTypes.string,
  disclosureAcceptLabel: PropTypes.string,
  disclosureCancelLabel: PropTypes.string,
  disclosureCloseLabel: PropTypes.string,
  fieldIsRequired: PropTypes.bool.isRequired,
  id: PropTypes.number.isRequired,
  isReadOnly: PropTypes.bool,
  title: PropTypes.string,
  validate: PropTypes.func.isRequired,
  viewTheEntireDocumentLabel: PropTypes.string,
};

DefaultBare.defaultProps = {
  clientId: '',
  differentiator: '',
  disclosureAcceptLabel: 'Accept',
  disclosureCancelLabel: 'Cancel',
  disclosureCloseLabel: 'Close',
  isReadOnly: false,
  title: '',
  viewTheEntireDocumentLabel: 'View the entire document to accept the terms.',
};

const mapStateToProps = ({ dialogs, fields, resources }) => ({
  clientId: dialogs.params.clientId,
  differentiator: dialogs.params.differentiator,
  disclosureAcceptLabel: resources.disclosureAcceptLabel,
  disclosureCancelLabel: resources.disclosureCancelLabel,
  disclosureCloseLabel: resources.disclosureCloseLabel,
  fieldIsRequired: dialogs.params.clientId ? fields[dialogs.params.clientId].required : false,
  id: dialogs.params.id,
  isReadOnly: dialogs.params.readOnly,
  title: dialogs.params.clientId ? fields[dialogs.params.clientId].label : false,
  viewTheEntireDocumentLabel: resources.viewTheEntireDocumentLabel,
});

const DefaultStyled = withStyles(styles)(DefaultBare);

export default connect(mapStateToProps, {
  changeFieldValue: dataActions.changeData,
  close: dialogActions.close,
  validate: fieldActions.validate,
})(DefaultStyled);
